import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { formatTL, formatDateTR } from "@/lib/format";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, Legend, BarChart, Bar, CartesianGrid,
} from "recharts";
import {
  Wallet, ArrowDownRight, ArrowUpRight, TrendingDown, CalendarClock, Sparkles,
  CheckCircle2, Clock, AlertTriangle,
} from "lucide-react";

const COLORS = ["#8A5A44", "#D4A373", "#4A6741", "#E9C46A", "#264653", "#B24C4C"];

const TONE_CLASSES = {
  success: "bg-[#4A6741]/10 text-[#4A6741]",
  danger: "bg-[#B24C4C]/10 text-[#B24C4C]",
  warning: "bg-[#D4A373]/20 text-[#8A5A44]",
  neutral: "bg-[#8A5A44]/10 text-[#8A5A44]",
};

const StatCard = ({ label, value, hint, icon: Icon, tone = "neutral", testid }) => (
  <div className="bg-white border border-[#E6E4DD] rounded-2xl p-6 hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)] transition-shadow" data-testid={testid}>
    <div className="flex items-start justify-between mb-4">
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5B57]">{label}</p>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${TONE_CLASSES[tone] || TONE_CLASSES.neutral}`}>
        <Icon size={18} strokeWidth={1.75} />
      </div>
    </div>
    <p className="font-heading text-3xl text-[#2D2C2A] mb-1 tracking-tight">{formatTL(value)}</p>
    {hint && <p className="text-sm text-[#5C5B57]">{hint}</p>}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/dashboard/summary");
    setData(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-12 text-[#5C5B57]">Yükleniyor…</div>;

  const today = new Date();
  const in7 = new Date(); in7.setDate(today.getDate() + 7);
  const urgent = (data.upcoming || []).filter((u) => {
    try {
      const d = new Date(u.date);
      return d <= in7;
    } catch { return false; }
  });
  const urgentIn = urgent.filter((u) => u.direction === "incoming").reduce((s, u) => s + u.amount, 0);
  const urgentOut = urgent.filter((u) => u.direction === "outgoing").reduce((s, u) => s + u.amount, 0);

  const vis = data.visibility || { cheques: true, receivables: true, expenses: true, recurring: true, bank_accounts: true };

  return (
    <div className="p-6 md:p-10 lg:p-12 fade-in max-w-[1600px]">
      <div className="mb-10">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5B57] mb-2">Kontrol Paneli</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-[#2D2C2A] tracking-tight">Nakit Akışı</h1>
        <p className="text-[#5C5B57] mt-2">Tüm finansal durumunuz tek ekranda.</p>
      </div>

      {urgent.length > 0 && (
        <div className="mb-6 bg-[#D4A373]/20 border-l-4 border-[#8A5A44] rounded-2xl p-5 flex items-start gap-4" data-testid="urgent-alert-banner">
          <div className="w-10 h-10 rounded-xl bg-[#8A5A44] text-white flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={20} strokeWidth={1.75} />
          </div>
          <div className="flex-1 flex flex-wrap items-baseline justify-between gap-4">
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#8A5A44] mb-1">7 gün içinde</p>
              <p className="font-heading text-lg text-[#2D2C2A]">
                <strong data-testid="urgent-count">{urgent.length}</strong> yaklaşan hareket var
              </p>
            </div>
            <div className="flex gap-6 text-sm">
              <span className="text-[#4A6741]">Gelen: <strong className="font-mono">{formatTL(urgentIn)}</strong></span>
              <span className="text-[#B24C4C]">Giden: <strong className="font-mono">{formatTL(urgentOut)}</strong></span>
            </div>
          </div>
        </div>
      )}

      {/* North Star + KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {vis.bank_accounts && (
        <div className="lg:col-span-1 bg-gradient-to-br from-[#8A5A44] to-[#714835] text-white rounded-2xl p-8 relative overflow-hidden" data-testid="total-balance-card">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
          <div className="absolute right-8 bottom-8 w-24 h-24 rounded-full bg-white/5" />
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-white/70 mb-4 relative">Toplam Nakit</p>
          <p className="font-heading text-5xl mb-2 relative">{formatTL(data.total_balance)}</p>
          <p className="text-white/80 text-sm relative">{data.counts.accounts} banka hesabı</p>
          <div className="mt-6 pt-6 border-t border-white/20 relative">
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-white/70 mb-2 flex items-center gap-1"><Sparkles size={12}/> 30 gün projeksiyon</p>
            <p className="font-heading text-2xl">{formatTL(data.projected_net)}</p>
          </div>
        </div>
        )}

        <div className={`${vis.bank_accounts ? "lg:col-span-2" : "lg:col-span-3"} grid grid-cols-1 sm:grid-cols-2 gap-6`}>
          {vis.cheques && <StatCard testid="kpi-incoming-cheques" label="Gelen Çekler" value={data.incoming_cheques} hint="Bekleyen tahsilat" icon={ArrowDownRight} tone="success" />}
          {vis.cheques && <StatCard testid="kpi-outgoing-cheques" label="Çıkan Çekler" value={data.outgoing_cheques} hint="Bekleyen ödeme" icon={ArrowUpRight} tone="danger" />}
          {vis.receivables && <StatCard testid="kpi-receivables" label="Alacaklar" value={data.pending_receivables} hint="Bekleyen alacaklar" icon={Wallet} tone="warning" />}
          {vis.expenses && <StatCard testid="kpi-month-expenses" label="Aylık Gider" value={data.month_expenses} hint="Bu ay toplam" icon={TrendingDown} tone="neutral" />}
        </div>
      </div>

      {/* Chart + upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white border border-[#E6E4DD] rounded-2xl p-6 md:p-8" data-testid="cashflow-chart-card">
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5B57] mb-2">30 Günlük Akış</p>
              <h2 className="font-heading text-2xl text-[#2D2C2A]">Nakit Girişi & Çıkışı</h2>
            </div>
          </div>
          {data.cashflow.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-[#5C5B57] text-sm">
              Henüz yaklaşan hareket yok. Çek veya alacak ekleyerek başlayın.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={data.cashflow}>
                <defs>
                  <linearGradient id="cin" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4A6741" stopOpacity={0.35}/>
                    <stop offset="100%" stopColor="#4A6741" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="cout" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#B24C4C" stopOpacity={0.3}/>
                    <stop offset="100%" stopColor="#B24C4C" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fill: "#5C5B57", fontSize: 11 }} tickFormatter={(v) => formatDateTR(v).slice(0, 6)} stroke="#E6E4DD" />
                <YAxis tick={{ fill: "#5C5B57", fontSize: 11 }} stroke="#E6E4DD" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatTL(v)} labelFormatter={formatDateTR}
                  contentStyle={{ background: "#fff", border: "1px solid #E6E4DD", borderRadius: 12, fontSize: 12 }} />
                <Area type="monotone" dataKey="in" name="Giriş" stroke="#4A6741" strokeWidth={2} fill="url(#cin)" />
                <Area type="monotone" dataKey="out" name="Çıkış" stroke="#B24C4C" strokeWidth={2} fill="url(#cout)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-[#E6E4DD] rounded-2xl p-6" data-testid="upcoming-payments-card">
          <div className="flex items-center gap-2 mb-4">
            <CalendarClock size={18} className="text-[#8A5A44]" strokeWidth={1.75} />
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5B57]">Yaklaşan Ödemeler</p>
          </div>
          {data.upcoming.length === 0 ? (
            <p className="text-sm text-[#5C5B57] py-8 text-center">30 gün içinde vadesi gelen bir kayıt yok.</p>
          ) : (
            <ul className="space-y-3 max-h-[280px] overflow-y-auto">
              {data.upcoming.map((u) => (
                <li key={`${u.type}-${u.date}-${u.title}`} className="flex items-center justify-between p-3 rounded-xl hover:bg-[#F0EFEA]/60 transition-colors" data-testid={`upcoming-item-${u.type}-${u.date}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#2D2C2A] truncate">{u.title}</p>
                    <p className="text-xs text-[#5C5B57]">{formatDateTR(u.date)}</p>
                  </div>
                  <span className={`text-sm font-mono font-medium ${u.direction === "incoming" ? "text-[#4A6741]" : "text-[#B24C4C]"}`}>
                    {u.direction === "incoming" ? "+" : "-"}{formatTL(u.amount)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Incoming (Gelen Tutarlar) Summary */}
      {(vis.cheques || vis.receivables) && (
      <div className="bg-white border border-[#E6E4DD] rounded-2xl p-6 md:p-8 mb-6" data-testid="incoming-summary-card">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5B57] mb-2">Gelen Tutarlar</p>
            <h2 className="font-heading text-2xl text-[#2D2C2A]">Nakit Girişi Özeti</h2>
          </div>
          <div className="text-right">
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5B57]">Genel Toplam</p>
            <p className="font-heading text-3xl text-[#4A6741]" data-testid="incoming-grand-total">
              {formatTL(data.incoming_summary?.grand_total || 0)}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-[#F0EFEA] rounded-xl p-5" data-testid="incoming-pending-cheques">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={14} className="text-[#8A5A44]" strokeWidth={2} />
              <p className="text-xs uppercase tracking-[0.12em] font-bold text-[#5C5B57]">Gelen Çekler (Bekleyen)</p>
            </div>
            <p className="font-heading text-xl text-[#2D2C2A]">{formatTL(data.incoming_summary?.pending_cheques || 0)}</p>
          </div>
          <div className="bg-[#F0EFEA] rounded-xl p-5" data-testid="incoming-pending-receivables">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={14} className="text-[#8A5A44]" strokeWidth={2} />
              <p className="text-xs uppercase tracking-[0.12em] font-bold text-[#5C5B57]">Alacaklar (Bekleyen)</p>
            </div>
            <p className="font-heading text-xl text-[#2D2C2A]">{formatTL(data.incoming_summary?.pending_receivables || 0)}</p>
          </div>
          <div className="bg-[#4A6741]/10 rounded-xl p-5" data-testid="incoming-collected-cheques">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 size={14} className="text-[#4A6741]" strokeWidth={2} />
              <p className="text-xs uppercase tracking-[0.12em] font-bold text-[#4A6741]">Tahsil Edilen Çekler</p>
            </div>
            <p className="font-heading text-xl text-[#2D2C2A]">{formatTL(data.incoming_summary?.collected_cheques || 0)}</p>
          </div>
          <div className="bg-[#4A6741]/10 rounded-xl p-5" data-testid="incoming-collected-receivables">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 size={14} className="text-[#4A6741]" strokeWidth={2} />
              <p className="text-xs uppercase tracking-[0.12em] font-bold text-[#4A6741]">Tahsil Edilen Alacaklar</p>
            </div>
            <p className="font-heading text-xl text-[#2D2C2A]">{formatTL(data.incoming_summary?.collected_receivables || 0)}</p>
          </div>
        </div>
      </div>
      )}

      {/* Monthly Bar Chart */}
      {(vis.cheques || vis.receivables || vis.expenses) && (
      <div className="bg-white border border-[#E6E4DD] rounded-2xl p-6 md:p-8 mb-6" data-testid="monthly-bar-chart-card">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5B57] mb-2">7 Ay</p>
            <h2 className="font-heading text-2xl text-[#2D2C2A]">Aylık Gelir & Gider</h2>
            <p className="text-sm text-[#5C5B57] mt-1">Geçmiş 3 ay + şu an + gelecek 3 ay projeksiyonu</p>
          </div>
          <div className="flex items-center gap-4 text-xs text-[#5C5B57]">
            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-[#4A6741]" /> Gelen</span>
            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-[#B24C4C]" /> Giden</span>
          </div>
        </div>
        {(data.monthly_breakdown || []).length === 0 ? (
          <div className="h-64 flex items-center justify-center text-[#5C5B57] text-sm">Veri yok.</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.monthly_breakdown} barGap={6}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E4DD" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#5C5B57", fontSize: 12 }} stroke="#E6E4DD" />
              <YAxis tick={{ fill: "#5C5B57", fontSize: 11 }} stroke="#E6E4DD" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(v, name) => [formatTL(v), name === "in" ? "Gelen" : "Giden"]}
                contentStyle={{ background: "#fff", border: "1px solid #E6E4DD", borderRadius: 12, fontSize: 12 }}
              />
              <Bar dataKey="in" name="Gelen" fill="#4A6741" radius={[8, 8, 0, 0]} />
              <Bar dataKey="out" name="Giden" fill="#B24C4C" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      )}

      {/* Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {vis.expenses && (
        <div className="bg-white border border-[#E6E4DD] rounded-2xl p-6 md:p-8" data-testid="expense-categories-card">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5B57] mb-2">Bu ay</p>
          <h2 className="font-heading text-2xl text-[#2D2C2A] mb-6">Gider Kategorileri</h2>
          {data.expense_categories.length === 0 ? (
            <p className="text-sm text-[#5C5B57] py-8 text-center">Henüz gider kaydı yok.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={data.expense_categories} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={3}>
                  {data.expense_categories.map((entry) => <Cell key={entry.category} fill={COLORS[data.expense_categories.indexOf(entry) % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatTL(v)} contentStyle={{ background: "#fff", border: "1px solid #E6E4DD", borderRadius: 12, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12, color: "#5C5B57" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        )}

        <div className="bg-[#F0EFEA] border border-[#E6E4DD] rounded-2xl p-6 md:p-8" data-testid="quick-stats-card">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5B57] mb-2">Özet</p>
          <h2 className="font-heading text-2xl text-[#2D2C2A] mb-6">Hızlı Bakış</h2>
          <div className="grid grid-cols-2 gap-4">
            {[
              { key: "bank_accounts", label: "Banka Hesabı", value: data.counts.accounts },
              { key: "cheques", label: "Çek", value: data.counts.cheques },
              { key: "receivables", label: "Alacak", value: data.counts.receivables },
              { key: "expenses", label: "Gider Kaydı", value: data.counts.expenses },
            ].filter((s) => vis[s.key]).map((s) => (
              <div key={s.label} className="bg-white rounded-xl p-4 border border-[#E6E4DD]">
                <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#5C5B57]">{s.label}</p>
                <p className="font-heading text-3xl text-[#2D2C2A] mt-1">{s.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
